import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Compass, Send } from "lucide-react";
import { toast } from "sonner";
import { WeaveLogo } from "@/components/WeaveLogo";
import { MaskedLine, FadeUp, Overline } from "@/components/Motion";
import { registerInterest } from "@/lib/api";
import { DepositButton } from "@/components/DepositButton";
import { ProspectusDialog } from "@/components/ProspectusDialog";

const TIDYCAL_URL = "https://tidycal.com/the-weave/30-minute-meeting";

const PHASES = [
  {
    n: "01",
    name: "Ground",
    text: "Sophrology-informed mindset practice builds the inner stability and sustainable rhythm everything else stands on. You learn to pause, recover, and lead yourself before you lead anything else.",
  },
  {
    n: "02",
    name: "Explore",
    text: "From a cloudy sense of what you want to change to genuine clarity — your values, your direction, and the shape of a business that would actually fit your life.",
  },
  {
    n: "03",
    name: "Test",
    text: "Small, real-world experiments with real customers. Your side hustle leaves the notebook and meets the market, gathering evidence instead of opinions.",
  },
  {
    n: "04",
    name: "Build",
    text: "Strategy, operating rhythm, and the makings of a solid business — built at a pace a full life can sustain, alongside peers making the same transition.",
  },
];

const PILLARS = [
  "Sophrology-informed mindset and leadership practice",
  "Fortnightly real-world experiments with customer evidence",
  "Mentoring and a practitioner network",
  "A peer community of people making the same transition",
  "Practical business education — no startup theatre",
  "A flexible, modular rhythm that fits around work and family",
];

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-weave-cream/80 backdrop-blur-xl border-b border-weave-line">
    <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
      <Link to="/" aria-label="The Weave home"><WeaveLogo /></Link>
      <Link
        to="/assessment"
        data-testid="academy-header-start-button"
        className="hidden sm:inline-flex items-center gap-2 bg-weave-teal text-white hover:bg-weave-tealdeep transition-colors duration-300 rounded-full px-6 py-2.5 text-sm font-medium"
      >
        Discover your founder capacity <ArrowRight size={16} />
      </Link>
    </div>
  </header>
);

const RegisterCard = () => {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Please add your email so we can keep you posted.");
      return;
    }
    setBusy(true);
    try {
      await registerInterest({ type: "register_interest", ...form });
      setDone(true);
      toast.success("You're on the list for the April 2027 cohort.");
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <FadeUp delay={0.15} className="bg-weave-cream text-weave-ink rounded-2xl p-8 md:p-10" data-testid="academy-register-card">
      <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-weave-red bg-weave-red/10 rounded-full px-4 py-2">
        First cohort · April 2027
      </span>
      <div className="mt-6 rounded-xl border border-weave-line bg-white p-5">
        <p className="text-sm text-weave-slate leading-relaxed">
          <strong className="text-weave-ink font-medium">Not sure whether founding is your next chapter?</strong>{" "}
          You don’t need to decide that today.
        </p>
        <p className="mt-2 text-sm text-weave-slate leading-relaxed">
          Personal Clarity gives you ten structured weeks — including the Founder Capacity Index —
          to explore what you want, understand what you bring, and work out whether building
          something of your own genuinely fits your life.
        </p>
        <p className="mt-2 text-sm text-weave-slate leading-relaxed">
          <span className="text-weave-red font-medium">£499 including VAT.</span> If you decide
          you want to build something, £2,501 secures the remaining Business and External
          Clarity sections, bringing the full journey to £3,000.
        </p>
        <div className="mt-4 flex flex-col items-start gap-3">
          <DepositButton email={form.email} plan="clarity" testId="academy-clarity-button" />
        </div>
      </div>
      <h3 className="mt-6 text-xl md:text-2xl font-medium">Register your interest</h3>
      <p className="mt-3 text-sm text-weave-slate leading-relaxed">
        Leave your details and we will keep you posted as the first cohort takes shape. No
        obligation, no hard sell.
      </p>
      {done ? (
        <div className="mt-8 flex items-center gap-3 text-weave-teal" data-testid="academy-register-success">
          <CheckCircle2 size={22} />
          <p className="font-medium">Thank you — you’re on the list.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="academy-register-form">
          <input
            type="text"
            placeholder="First name (optional)"
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            data-testid="academy-register-name-input"
            className="w-full rounded-xl border border-weave-line bg-white px-4 py-3 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none transition-colors duration-200"
          />
          <input
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="academy-register-email-input"
            className="w-full rounded-xl border border-weave-line bg-white px-4 py-3 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none transition-colors duration-200"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="academy-register-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-weave-red text-white hover:bg-[#cf352e] transition-colors duration-300 rounded-full px-8 py-4 font-medium disabled:opacity-60"
          >
            {busy ? "Registering…" : "Keep me posted"} <Send size={16} />
          </button>
        </form>
      )}
    </FadeUp>
  );
};

export default function Academy() {
  return (
    <div data-testid="academy-page">
      <Header />

      <section className="pt-40 pb-24 md:pb-32" data-testid="academy-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Overline>The Weave Founder Academy</Overline>
          <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-weave-ink leading-[1.08] max-w-4xl" data-testid="academy-headline">
            <MaskedLine delay={0.1}>A year to build what matters,</MaskedLine>
            <MaskedLine delay={0.25}><span className="text-weave-teal font-normal">without losing yourself in the process.</span></MaskedLine>
          </h1>
          <FadeUp delay={0.4} className="mt-8 max-w-2xl">
            <p className="text-lg md:text-xl text-weave-slate leading-relaxed">
              The Founder Academy is a practice ground for experienced professionals who want more
              than a course. Over twelve months, you’ll develop the mindset of a founder, turn a
              cloudy sense of what you want to change into a tested side hustle, and lay the
              foundations of a business that is genuinely yours.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#academy-register"
                data-testid="academy-hero-register-button"
                className="inline-flex items-center gap-3 bg-weave-teal text-white hover:bg-weave-tealdeep transition-colors duration-300 rounded-full px-8 py-4 font-medium tracking-wide"
              >
                Register your interest <ArrowRight size={18} />
              </a>
              <ProspectusDialog
                triggerText="Download the prospectus"
                testId="academy-hero-prospectus-button"
                triggerClassName="inline-flex items-center gap-3 bg-weave-yellow text-weave-ink hover:bg-[#e5ab38] transition-colors duration-300 rounded-full px-8 py-4 font-medium"
              />
              <a
                href={TIDYCAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="academy-hero-consult-link"
                className="inline-flex items-center gap-2 bg-transparent border border-weave-teal text-weave-teal hover:bg-weave-teal/10 transition-colors duration-300 rounded-full px-8 py-4 font-medium"
              >
                Book a 30-minute Clarity Consult
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-weave-sand/60 border-y border-weave-line" data-testid="academy-who">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <FadeUp>
              <Overline>01 — Who it is for</Overline>
              <h2 className="mt-6 text-3xl md:text-4xl font-normal tracking-tight text-weave-ink leading-tight">
                Built for people with a career behind them — and plenty ahead.
              </h2>
            </FadeUp>
          </div>
          <div className="md:col-span-7">
            <FadeUp delay={0.1}>
              <p className="text-lg md:text-xl text-weave-slate leading-relaxed">
                The Academy is designed for experienced professionals — people who have led teams,
                delivered real work, and now want that experience to serve something of their own.
                You will not be asked to delete your past or start from zero. Everything here is
                peer-to-peer: your decades in the room are the raw material, not the obstacle.
              </p>
              <p className="mt-6 text-lg md:text-xl text-weave-slate leading-relaxed">
                If you have wondered whether your best working years could still be ahead of you,
                they can be. This is where you prove it — steadily, with evidence, alongside people
                making the same move.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="academy-year">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeUp>
            <Overline>02 — The year, in practice</Overline>
            <h2 className="mt-6 text-3xl md:text-4xl font-normal tracking-tight text-weave-ink leading-tight max-w-2xl">
              Twelve months. Four movements. One business taking shape.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14">
            {PHASES.map((p, i) => (
              <FadeUp key={p.n} delay={(i % 2) * 0.08} className="bg-white border border-weave-line rounded-2xl p-8 md:p-10" data-testid={`phase-${p.n}`}>
                <span className="text-sm font-bold text-weave-red tracking-widest">{p.n}</span>
                <h3 className="mt-3 text-xl md:text-2xl font-medium text-weave-ink">{p.name}</h3>
                <p className="mt-3 text-base text-weave-slate leading-relaxed">{p.text}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section id="academy-register" className="py-24 md:py-32 bg-weave-ink text-weave-cream" data-testid="academy-join">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-7">
            <FadeUp>
              <Overline dark>03 — What is woven in</Overline>
              <h2 className="mt-6 text-3xl md:text-4xl font-light tracking-tight leading-tight">
                Not a course. <span className="text-weave-yellow font-normal">A practice ground.</span>
              </h2>
              <ul className="mt-10 space-y-5 max-w-xl">
                {PILLARS.map((item) => (
                  <li key={item} className="flex gap-4 items-start">
                    <Compass size={19} className="mt-1 shrink-0 text-weave-teal" />
                    <span className="text-base md:text-lg text-weave-cream/85 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-10 text-xl font-light text-weave-cream tracking-wide">
                Find clarity. Test possibility. <span className="text-weave-teal">Build what matters.</span>
              </p>
            </FadeUp>
          </div>
          <div className="md:col-span-5">
            <RegisterCard />
          </div>
        </div>
      </section>

      <footer className="bg-weave-ink text-weave-cream border-t border-weave-cream/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <WeaveLogo dark />
            <p className="mt-4 text-sm text-weave-cream/70 max-w-sm leading-relaxed">
              Founder First; Business Second. Always.
            </p>
            <a
              href="https://www.wearetheweave.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="academy-footer-website-button"
              className="group mt-6 inline-flex items-center gap-2 border border-weave-cream/30 text-weave-cream hover:bg-weave-cream hover:text-weave-ink transition-colors duration-300 rounded-full px-6 py-3 text-sm font-medium"
            >
              Find out more about The Weave
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
          <Link to="/" className="group inline-flex items-center gap-2 text-weave-teal font-medium" data-testid="academy-back-home-link">
            Back to the Founder Capacity Index
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="border-t border-weave-cream/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
            <p className="text-xs text-weave-cream/40 leading-relaxed" data-testid="academy-footer-legal">
              Relocon t/a The Weave Limited · Company Registration Number 12213782 · VAT Registration
              Number 335471503 ·{" "}
              <a href="https://www.wearetheweave.co.uk" target="_blank" rel="noopener noreferrer" className="underline hover:text-weave-cream/70 transition-colors duration-200">
                www.wearetheweave.co.uk
              </a>{" "}
              ·{" "}
              <a href="https://www.wearetheweave.co.uk/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="academy-footer-privacy-link" className="underline hover:text-weave-cream/70 transition-colors duration-200">
                Privacy Notice
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
